<template>
  <div class="property-deduction-share-ratio-config">
    <el-card>
      <div slot="header">抵扣金成本分担比例配置</div>

      <div class="query-bar">
        <el-form :inline="true" :model="query" size="small">
          <el-form-item label="配置范围">
            <el-select v-model="query.scopeType" placeholder="全部" clearable style="width: 160px;">
              <el-option label="平台" value="PLATFORM" />
              <el-option label="小区" value="COMMUNITY" />
              <el-option label="店铺" value="SHOP" />
            </el-select>
          </el-form-item>
          <el-form-item label="范围对象ID">
            <el-input v-model="query.scopeId" placeholder="选择小区/店铺后填写对应ID" clearable style="width: 200px;" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="queryConfigs(1)">查询</el-button>
            <el-button @click="resetQuery">重置</el-button>
            <el-button type="success" @click="openAddDialog">新增配置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <el-table :data="configs" border stripe style="width: 100%">
        <el-table-column prop="scopeType" label="配置范围" align="center">
          <template #default="{ row }">{{ formatScopeType(row.scopeType) }}</template>
        </el-table-column>
        <el-table-column prop="scopeId" label="范围对象ID" align="center">
          <template #default="{ row }">{{ row.scopeId || '-' }}</template>
        </el-table-column>
        <el-table-column prop="merchantRatio" label="商家承担比例" align="center">
          <template #default="{ row }">{{ formatRatio(row.merchantRatio) }}</template>
        </el-table-column>
        <el-table-column prop="propertyRatio" label="物业承担比例" align="center">
          <template #default="{ row }">{{ formatRatio(row.propertyRatio) }}</template>
        </el-table-column>
        <el-table-column prop="statusCd" label="状态" align="center" width="100">
          <template #default="{ row }">{{ formatStatus(row.statusCd) }}</template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" align="center" />
        <el-table-column label="操作" align="center" width="150">
          <template #default="{ row }">
            <el-button type="text" size="small" @click="editConfig(row)">编辑</el-button>
            <el-button type="text" size="small" style="color: #f56c6c;" @click="deleteConfig(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page="query.page"
        :page-size="query.row"
        :total="total"
        @current-change="queryConfigs"
      />
    </el-card>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false">
      <el-form ref="form" :model="form" :rules="rules" label-width="140px" size="small">
        <el-form-item label="配置范围" prop="scopeType">
          <el-tooltip effect="dark" placement="top">
            <div slot="content">
              平台：全局默认配置，对所有小区/店铺生效<br>
              小区：仅对指定小区生效，需填写小区ID<br>
              店铺：仅对指定店铺生效，需填写店铺ID
            </div>
            <el-select v-model="form.scopeType" placeholder="请选择配置范围" style="width: 100%;" @change="onScopeTypeChange">
              <el-option label="平台" value="PLATFORM" />
              <el-option label="小区" value="COMMUNITY" />
              <el-option label="店铺" value="SHOP" />
            </el-select>
          </el-tooltip>
        </el-form-item>
        <el-form-item v-if="form.scopeType !== 'PLATFORM'" :label="scopeIdLabel" prop="scopeId">
          <el-input v-model="form.scopeId" :placeholder="'请输入' + scopeIdLabel" clearable />
        </el-form-item>
        <el-form-item v-else label="范围对象ID">
          <el-input value="平台级默认配置，无需填写" disabled />
        </el-form-item>
        <el-form-item label="商家承担比例" prop="merchantRatio">
          <el-input-number
            v-model="form.merchantRatio"
            :precision="4"
            :min="0"
            :max="1"
            :step="0.01"
            controls-position="right"
            @change="calcPropertyRatio"
          />
          <span style="margin-left: 8px;">{{ formatRatio(form.merchantRatio) }}</span>
        </el-form-item>
        <el-form-item label="物业承担比例">
          <el-input-number v-model="form.propertyRatio" :precision="4" :disabled="true" controls-position="right" />
          <span style="margin-left: 8px;">{{ formatRatio(form.propertyRatio) }}</span>
          <div class="form-tip">物业承担比例自动计算：1 - 商家承担比例</div>
        </el-form-item>
        <el-form-item v-if="isEdit" label="状态" prop="statusCd">
          <el-radio-group v-model="form.statusCd">
            <el-radio label="0">有效</el-radio>
            <el-radio label="1">无效</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveConfig">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'

const CMD_URL = '/propertyDeduction.shareRatioConfig'

export default {
  name: 'ShareRatioConfig',
  data() {
    const validateShareSum = (rule, value, callback) => {
      const merchant = parseFloat(this.form.merchantRatio)
      const property = parseFloat(this.form.propertyRatio)
      if (isNaN(merchant) || isNaN(property)) {
        callback(new Error('请填写完整的分担比例'))
        return
      }
      const sum = Number((merchant + property).toFixed(4))
      if (sum !== 1) {
        callback(new Error('商家与物业承担比例之和必须等于 1'))
        return
      }
      callback()
    }

    return {
      query: {
        scopeType: '',
        scopeId: '',
        page: 1,
        row: 10
      },
      configs: [],
      total: 0,
      dialogVisible: false,
      form: {
        shareId: '',
        scopeType: '',
        scopeId: '',
        merchantRatio: 0,
        propertyRatio: 1,
        statusCd: '0'
      },
      rules: {
        scopeType: [{ required: true, message: '请选择配置范围', trigger: 'change' }],
        scopeId: [{ required: true, message: '请填写范围对象ID', trigger: 'blur' }],
        merchantRatio: [
          { required: true, message: '请输入商家承担比例', trigger: 'blur' },
          { validator: validateShareSum, trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.form.shareId
    },
    dialogTitle() {
      return this.isEdit ? '编辑成本分担比例配置' : '新增成本分担比例配置'
    },
    scopeIdLabel() {
      if (this.form.scopeType === 'COMMUNITY') return '小区ID'
      if (this.form.scopeType === 'SHOP') return '店铺ID'
      return '范围对象ID'
    }
  },
  mounted() {
    this.queryConfigs(1)
  },
  methods: {
    queryConfigs(page) {
      if (page) {
        this.query.page = page
      }
      request({
        url: CMD_URL,
        method: 'post',
        data: {
          action: 'query',
          scopeType: this.query.scopeType,
          scopeId: this.query.scopeId,
          page: this.query.page,
          row: this.query.row
        }
      }).then(response => {
        const result = response.data || {}
        const res = result.data || {}
        this.configs = res.configs || []
        this.total = res.total || this.configs.length
      }).catch(() => {
        this.$message.error('查询配置失败')
      })
    },
    resetQuery() {
      this.query.scopeType = ''
      this.query.scopeId = ''
      this.query.page = 1
      this.queryConfigs(1)
    },
    openAddDialog() {
      this.form = {
        shareId: '',
        scopeType: '',
        scopeId: '',
        merchantRatio: 0,
        propertyRatio: 1,
        statusCd: '0'
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    editConfig(row) {
      const merchant = row.merchantRatio == null ? 0 : Number(row.merchantRatio)
      this.form = {
        shareId: row.shareId,
        scopeType: row.scopeType || '',
        scopeId: row.scopeId || '',
        merchantRatio: merchant,
        propertyRatio: row.propertyRatio == null ? Number((1 - merchant).toFixed(4)) : Number(row.propertyRatio),
        statusCd: row.statusCd || '0'
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    saveConfig() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const action = this.isEdit ? 'update' : 'save'
        const payload = {
          action,
          shareId: this.form.shareId,
          scopeType: this.form.scopeType,
          scopeId: this.form.scopeType === 'PLATFORM' ? '' : this.form.scopeId,
          merchantRatio: this.form.merchantRatio,
          propertyRatio: this.form.propertyRatio
        }
        if (this.isEdit) {
          payload.statusCd = this.form.statusCd
        }
        request({
          url: CMD_URL,
          method: 'post',
          data: payload
        }).then(() => {
          this.$message.success('保存成功')
          this.dialogVisible = false
          this.queryConfigs(1)
        }).catch(() => {
          this.$message.error('保存失败')
        })
      })
    },
    deleteConfig(row) {
      this.$confirm('确认删除该成本分担比例配置？', '提示', { type: 'warning' }).then(() => {
        request({
          url: CMD_URL,
          method: 'post',
          data: {
            action: 'delete',
            shareId: row.shareId
          }
        }).then(() => {
          this.$message.success('删除成功')
          this.queryConfigs(1)
        }).catch(() => {
          this.$message.error('删除失败')
        })
      }).catch(() => {})
    },
    onScopeTypeChange(scopeType) {
      if (scopeType === 'PLATFORM') {
        this.form.scopeId = ''
      }
    },
    calcPropertyRatio() {
      const merchant = parseFloat(this.form.merchantRatio) || 0
      let property = 1 - merchant
      if (property < 0) property = 0
      if (property > 1) property = 1
      this.form.propertyRatio = Number(property.toFixed(4))
      this.$refs.form && this.$refs.form.validateField('merchantRatio')
    },
    formatScopeType(scopeType) {
      if (scopeType === 'PLATFORM') return '平台'
      if (scopeType === 'COMMUNITY') return '小区'
      if (scopeType === 'SHOP') return '店铺'
      return scopeType || '-'
    },
    formatRatio(value) {
      if (value === undefined || value === null || value === '') return '-'
      const ratio = parseFloat(value) || 0
      return ratio + '（' + (ratio * 100).toFixed(2) + '%）'
    },
    formatStatus(statusCd) {
      if (statusCd === '0') return '有效'
      if (statusCd === '1') return '无效'
      return statusCd || '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.property-deduction-share-ratio-config {
  .query-bar {
    margin-bottom: 16px;
  }
  .pagination {
    margin-top: 16px;
    text-align: right;
  }
  .form-tip {
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
    margin-top: 4px;
  }
}
</style>
